import { useEffect, useState } from 'react';
import { Loader2, Mail, MessageSquare, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

type Channel = 'email' | 'sms';

type Template = {
  id: string;
  title: string;
  channel: 'email' | 'sms' | 'both';
  subject: string | null;
  body: string;
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channel: Channel;
  dealId: string;
  dealershipId: string;
  customerName: string;
  to: string;
  defaultSubject?: string;
  onSent?: () => void;
}

export function ComposeMessageDialog({
  open,
  onOpenChange,
  channel,
  dealId,
  dealershipId,
  customerName,
  to,
  defaultSubject,
  onSent,
}: Props) {
  const [subject, setSubject] = useState(defaultSubject ?? '');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);

  useEffect(() => {
    if (open) {
      setSubject(defaultSubject ?? '');
      setBody('');
    }
  }, [open, defaultSubject]);

  useEffect(() => {
    if (!open || !dealershipId) return;
    let mounted = true;
    (async () => {
      const { data } = await supabase
        .from('message_templates')
        .select('id, title, channel, subject, body')
        .eq('dealership_id', dealershipId)
        .eq('is_active', true)
        .in('channel', [channel, 'both'])
        .order('sort_order', { ascending: true })
        .limit(20);
      if (mounted) setTemplates((data ?? []) as Template[]);
    })();
    return () => { mounted = false; };
  }, [open, dealershipId, channel]);

  const applyTemplate = (t: Template) => {
    const firstName = customerName?.split(' ')[0] ?? '';
    const interpolate = (s: string) =>
      s.replace(/\{\{\s*first_name\s*\}\}/gi, firstName)
       .replace(/\{\{\s*customer_name\s*\}\}/gi, customerName ?? '');
    if (channel === 'email' && t.subject) setSubject(interpolate(t.subject));
    setBody(interpolate(t.body));
  };

  const Icon = channel === 'email' ? Mail : MessageSquare;
  const fnName = channel === 'email' ? 'send-rep-email' : 'send-rep-sms';
  const title = channel === 'email' ? 'Email customer' : 'Text customer';

  const handleSend = async () => {
    if (!to) {
      toast.error(channel === 'email' ? 'No customer email on file' : 'No customer phone on file');
      return;
    }
    if (!body.trim()) {
      toast.error('Message is empty');
      return;
    }
    setSending(true);
    const { data, error } = await supabase.functions.invoke(fnName, {
      body: {
        deal_id: dealId,
        dealership_id: dealershipId,
        to,
        subject: channel === 'email' ? subject || `Following up — ${customerName}` : undefined,
        body: body.trim(),
      },
    });
    setSending(false);

    const needsSetup = (data as any)?.needs_setup ?? false;
    if (error || needsSetup) {
      const msg =
        (data as any)?.error ||
        error?.message ||
        (channel === 'email'
          ? 'Connect your email account in Settings → Communications.'
          : 'Ask your manager to assign you a texting number in Settings → Communications.');
      toast.error(msg);
      return;
    }
    toast.success(channel === 'email' ? 'Email sent' : 'Text sent');
    onSent?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-primary" /> {title}
          </DialogTitle>
          <DialogDescription>
            Customer will reply directly to you — PullOver just logs it to the deal.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>To</Label>
            <Input value={to || ''} disabled placeholder={channel === 'email' ? 'no email on file' : 'no phone on file'} />
          </div>

          {templates.length > 0 && (
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Sparkles className="h-3 w-3" /> Quick templates
              </Label>
              <div className="flex flex-wrap gap-1.5">
                {templates.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => applyTemplate(t)}
                    className="text-xs px-2.5 py-1 rounded-full border border-border bg-muted/40 hover:bg-muted hover:border-primary/40 transition-colors"
                  >
                    {t.title}
                  </button>
                ))}
              </div>
            </div>
          )}

          {channel === 'email' && (
            <div className="space-y-1.5">
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={`Following up — ${customerName}`}
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="body">Message</Label>
            <Textarea
              id="body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={channel === 'email' ? 8 : 4}
              maxLength={channel === 'sms' ? 320 : undefined}
              placeholder={
                channel === 'sms'
                  ? `Hi ${customerName?.split(' ')[0] ?? ''}, quick update on your vehicle…`
                  : `Hi ${customerName?.split(' ')[0] ?? ''},\n\n`
              }
            />
            {channel === 'sms' && (
              <p className="text-xs text-muted-foreground">{body.length}/320</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={sending || !to || !body.trim()}>
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : channel === 'email' ? 'Send email' : 'Send text'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}